import type { en } from './en.ts';
import type { DictionaryShape } from './types.ts';

// Polski słownik. Kształt wymusza en.ts; tłumaczymy sens zdania, nie słowo po słowie.
// Imiona zostają w mianowniku (nie odmieniamy ich), stąd konstrukcje „odbiorca: Anna”.

export const pl: DictionaryShape<typeof en> = {
  studio: {
  "openReports": "Otwórz raporty"
},
  reports: {
  "tab": "Raporty",
  "title": "Raport przepływów",
  "hint": "Zaksięgowane wpływy i wydatki pomniejszone o zwroty. Pomijamy przelewy i płatności oczekujące. Podzielone transakcje liczymy według ich części; każdą walutę osobno.",
  "category": "Kategoria",
  "month": "Miesiąc",
  "merchant": "Sklep / odbiorca",
  "groupBy": "Grupowanie raportu",
  "unknownMerchant": "Bez nazwy odbiorcy",
  "count": "Transakcje",
  "netSpending": "Wydatki po zwrotach",
  "incomeShort": "wpływy {amount}",
  "spendingShort": "wydatki {amount}",
  "exportReport": "Eksportuj raport",
  "exportTransactions": "Eksportuj transakcje",
  "empty": "Brak zaksięgowanych wpływów lub wydatków dla tych filtrów.",
  "saveView": "Zapisz filtry",
  "viewName": "Nazwa widoku",
  "viewHint": "Zapis na tym urządzeniu, dla tego gospodarstwa. „Wybrany okres” podąża za okresem u góry; własny zakres dat pozostaje stały.",
  "viewError": "Podaj unikalną nazwę (do 60 znaków) i poprawny zakres dat. Możesz zapisać do 20 widoków.",
  "removeView": "Usuń zapisane filtry: {name}",
  "date": "Data",
  "status": "Status",
  "kind": "Rodzaj",
  "split": "Podział na kategorie",
  "range": "Zakres dat",
  "selectedPeriod": "Wybrany okres",
  "customRange": "Własny zakres",
  "from": "Od",
  "to": "Do",
  "allStatuses": "Wszystkie statusy",
  "booked": "Zaksięgowane",
  "pending": "Oczekujące",
  "dateError": "Wybierz poprawną datę początkową i końcową. Początek nie może być późniejszy niż koniec.",
  "loadError": "Nie udało się wczytać transakcji. Sumy mogą być niepełne.",
  "totalsHint": "Sumy obejmują tylko zaksięgowane wpisy. Zwroty pomniejszają wydatki; przelewy nie są wpływem ani wydatkiem. Eksport transakcji zawiera pełną kwotę każdego pasującego wpisu."
},
  goals: {
    title: 'Cele oszczędnościowe',
    subtitle: 'Nadaj oszczędnościom cel i zaplanuj miesięczne wpłaty.',
    add: 'Nowy cel',
    edit: 'Edytuj cel',
    name: 'Nazwa celu',
    nameExample: 'Poduszka finansowa, wakacje, wkład własny…',
    target: 'Kwota docelowa',
    currency: 'Waluta',
    source: 'Źródło postępu',
    manual: 'Kwota odkładana ręcznie',
    manualHint: 'Wpisz odłożoną kwotę. Jej zmiana nie tworzy transakcji.',
    accountHint: 'Całe bieżące saldo zasila ten cel. Konto można powiązać z jednym aktywnym celem.',
    unavailable: 'Wybierz inne konto: powiązane konto jest niedostępne lub już przypisane.',
    savedAmount: 'Już odłożone',
    monthly: 'Plan miesięczny',
    deadline: 'Termin (opcjonalnie)',
    deadlineShort: 'Termin celu',
    projectionHint:
      'Szacunek zakłada wpłaty co miesiąc, od miesiąca po dzisiejszej dacie, bez zysku z inwestycji. Plan nie wykonuje przelewów.',
    archive: 'Archiwizuj',
    restore: 'Przywróć cel',
    active: 'Aktywne cele',
    archived: 'Archiwum',
    saved: 'Cel zapisany na urządzeniu; zlecono synchronizację.',
    invalid: 'Sprawdź nazwę, kwoty, walutę, konto i datę. Kwota docelowa musi być większa od zera.',
    saveError: 'Nie udało się zapisać celu. Spróbuj ponownie.',
    remaining: 'Pozostało do odłożenia',
    estimated: 'Przewidywane osiągnięcie',
    needed: 'Aby zdążyć w terminie:',
    perMonth: ' / mies.',
    reached: 'Cel osiągnięty',
    behind: 'Plan wymaga korekty',
    inProgress: 'W trakcie',
    deadlineSoon:
      'Przed tym terminem nie przypada miesięczna wpłata. Zaplanuj wpłatę jednorazową lub zmień termin.',
    empty: 'Na co odkładasz?',
    emptyHint: 'Ustal kwotę, powiąż konto oszczędnościowe i sprawdź, ile odkładać co miesiąc.',
    emptyArchive: 'Brak zarchiwizowanych celów',
    archiveHint: 'Archiwum zachowuje ostatni zapisany postęp. Cele można przywracać.',
    loadError: 'Nie udało się wczytać celów oszczędnościowych.',
  },
  app: {
    name: 'Finance Dashboard',
  },
  common: {
    todayAt: 'dziś {time}',
    tomorrowAt: 'jutro {time}',
    yesterdayAt: 'wczoraj {time}',
    save: 'Zapisz',
    cancel: 'Anuluj',
    delete: 'Usuń',
    add: 'Dodaj',
    close: 'Zamknij',
    retry: 'Spróbuj ponownie',
    search: 'Szukaj',
    loading: 'Wczytywanie…',
    more: 'Więcej',
  },
  nav: {
    dashboard: 'Pulpit',
    accounts: 'Rachunki',
    transactions: 'Transakcje',
    cashFlow: 'Przepływy',
    budgets: 'Budżety',
    loan: 'Kredyt',
    loans: 'Kredyty',
    groupSpending: 'Wydatki',
    groupWealth: 'Majątek',
    groupShared: 'Wspólne finanse',
    countInPeriod: 'W wybranym okresie',
    people: 'Osoby',
    settlements: 'Rozliczenia',
    recurring: 'Stałe płatności',
    goals: 'Cele',
    netWorth: 'Majątek netto',
    simulation: "Kalkulatory",
    savings: 'Oszczędności i inwestycje',
    reports: 'Raporty',
    settings: 'Ustawienia',
    home: 'Start',
  },
  period: {
    week: 'Tydzień',
    month: 'Miesiąc',
    year: 'Rok',
    previous: 'Poprzedni okres',
    next: 'Następny okres',
    vsPreviousWeek: 'wobec poprzedniego tygodnia',
    vsPreviousMonth: 'wobec poprzedniego miesiąca',
    vsPreviousYear: 'wobec poprzedniego roku',
  },
  auth: {
    signInTitle: 'Zaloguj się',
    signInSubtitle: 'Prywatne centrum finansów właściciela.',
    email: 'E-mail',
    password: 'Hasło',
    signIn: 'Zaloguj',
    signingIn: 'Logowanie…',
    signOut: 'Wyloguj',
    invalidCredentials: 'Nieprawidłowy e-mail lub hasło.',
    sessionExpired: 'Sesja wygasła. Zaloguj się ponownie.',
  },
  onboarding: {
    title: 'Ustaw swoje gospodarstwo',
    language: 'Język',
    householdName: 'Nazwa gospodarstwa',
    householdNamePlaceholder: 'Dom',
    baseCurrency: 'Waluta główna',
    baseCurrencyHint: 'W tej walucie pokazujemy sumy, majątek netto i raporty.',
    people: 'Osoby',
    peopleHint: 'Dodaj wszystkich, z którymi dzielisz koszty. Loguje się tylko Ty — osoby nie potrzebują kont.',
    personName: 'Imię',
    addPerson: 'Dodaj osobę',
    thisIsMe: 'To ja',
    defaultSplit: 'Jak zwykle dzielicie wspólne koszty?',
    ownData: 'Moje gospodarstwo',
    demoData: 'Zacznij od danych przykładowych',
    demoDataHint: 'Realistyczne przykładowe gospodarstwo do obejrzenia — możesz je później usunąć.',
    finish: 'Przejdź do pulpitu',
  },
  // The banner's line on what each page shows (Shell, PageHeadingCopy).
  heading: {
    transactions: 'Wszystko, co weszło i wyszło w wybranym okresie',
    reports: 'Raporty wydatków i wpływów za wybrany okres',
    upcoming: 'Prognoza salda i płatności, które jeszcze przyjdą',
    recurring: 'Stałe płatności, które przypadają w wybranym okresie',
    budgets: 'Ile zostało z budżetów w wybranym okresie',
    settlements: 'Wspólne koszty i kto komu oddaje',
    accounts: 'Twoje konta, karty i ich salda',
    loans: 'Kredyty i to, co zostało do spłaty',
    loan: 'Harmonogram spłaty i to, co zostało',
    savings: 'Oszczędności, cele i inwestycje',
    netWorth: 'Majątek netto i jak się zmienia',
    simulation: "Symulacja rachunków na rok i kalkulator wypłaty",
    people: 'Osoby w gospodarstwie i zasady podziału kosztów',
    settings: 'Ustawienia aplikacji i gospodarstwa',
  },
  dashboard: {
    layout: {
      edit: 'Układ pulpitu',
      hint: 'Przeciągnij karty albo przesuń je strzałkami. Ten sam układ widzisz na wszystkich swoich urządzeniach.',
      hintBoard: 'Przeciągnij kartę na inne pola siatki; prawą krawędzią, dolną krawędzią albo narożnikiem zmienisz jej rozmiar o całe pola — każda karta ma najmniejszy i największy rozmiar, a jej treść dopasowuje się do pola. Ten sam układ widzisz na wszystkich swoich urządzeniach.',
      reset: 'Układ domyślny',
      done: 'Gotowe',
      up: 'Wyżej',
      down: 'Niżej',
      move: 'Przenieś (strzałki)',
      resize: 'Zmień rozmiar (strzałki)',
      hide: 'Ukryj',
      hidden: 'Ukryte karty:',
    },
    widgets: {
      metrics: 'Podsumowanie okresu',
      overview: 'Majątek i prognoza',
      flow: 'Przepływy',
      settlement: 'Rozliczenie',
      recent: 'Ostatnie transakcje',
      upcoming: 'Nadchodzące płatności',
      heatmap: 'Mapa wydatków',
      changes: 'Zmiany w wydatkach',
      savings: 'Oszczędności i inwestycje',
    },
    loadError: 'Nie udało się wczytać wszystkich danych',
    loadErrorHint: 'Podsumowanie może być nieaktualne. Spróbuj ponownie wczytać dane.',
    incompleteTotals: 'Podsumowanie nie obejmuje wszystkich transakcji',
    incompleteTotalsHint: 'Brak przeliczenia na {currency} dla {count} transakcji z tego lub poprzedniego okresu. Kwoty i porównania nie uwzględniają tych pozycji.',
    reviewMissing: 'Pokaż pominięte transakcje ({count})',
    historyRange: 'Historia salda',
    skipToContent: 'Przejdź do głównej treści',
    title: 'Pulpit',
    income: 'Wpływy',
    expenses: 'Wydatki',
    saved: 'Odłożono',
    cashFlow: 'Przepływy',
    recentTransactions: 'Ostatnie transakcje',
    settlement: 'Rozliczenie',
    savingsCard: 'Oszczędności i inwestycje',
    viewAll: 'Zobacz wszystko',
    spendingChangesEmpty: 'Na razie nie ma czego porównać.',
    newCategory: 'nowa',
    flowFromBalance: 'Z sald rachunków',
    flowOther: { one: 'Pozostałe ({count} kategoria)', few: 'Pozostałe ({count} kategorie)', many: 'Pozostałe ({count} kategorii)', other: 'Pozostałe ({count} kategorii)' },
    flowOtherIncome: 'Inny dochód',
    flowSavings: 'Oszczędności i inwestycje',
    flowSurplus: 'Zostało na koncie',
    flowStayedOn: 'Zostało na {account}',
    flowElsewhere: 'Inne wypływy',
    flowShareOfIncome: '{share} dochodu',
    flowDrill: 'Przepływ',
    flowCaptionCategories: 'Kategorie',
    flowPaceShort: 'tempo {amount}',
    flowBackCaption: 'Wróć do',
    flowBackHint: 'Wróć do całości',
    flowSide: 'Strona przepływu',
    flowWhereTo: 'Na co wydaję',
    flowWhereFrom: 'Dochód',
    flowCrumbs: 'Poziom rozkładu',
    flowAllSpending: 'Wszystkie wydatki',
    flowAllIncome: 'Cały dochód',
    flowGroupLabel: 'Grupa',
    flowEnter: 'Rozłóż na części',
    flowOpenEntries: 'Pokaż transakcje',
    flowDrillHint: 'Najedź na kafel, żeby zobaczyć jego wstęgę. Kliknij grupę, żeby wejść głębiej.',
    flowDrillHintIn: 'Kliknij kategorię, żeby zobaczyć jej transakcje.',
    flowEmpty: 'W tym okresie nie ma jeszcze wpływów ani wydatków.',
    changeWeek: 'w tym tygodniu',
    changeMonth: 'w tym miesiącu',
    changeYear: 'w tym roku',
    heroChart: 'Majątek netto w czasie',
    netFlow: 'Wpływy minus wydatki',
    netFlowHint: 'O tyle wpływy z tego okresu przewyższyły wydatki (razem z płatnościami oczekującymi). Odłożone na oszczędności nie jest wydatkiem, więc się tu nie odejmuje.',
    freshBank: 'Bank: pobrano {last}',
    freshBankNext: 'Bank: pobrano {last}, następnie {next}',
    freshInvest: 'Trading 212: {time}',
  },
  heatmap: {
    days: {
      mon: 'Pn',
      tue: 'Wt',
      wed: 'Śr',
      thu: 'Cz',
      fri: 'Pt',
      sat: 'Sb',
      sun: 'Nd',
    },
    title: 'Mapa wydatków',
    subtitle: 'Ostatnie 12 miesięcy, dzień po dniu',
    lastWeeks: { one: 'Ostatni {count} tydzień, dzień po dniu', few: 'Ostatnie {count} tygodnie, dzień po dniu', many: 'Ostatnie {count} tygodni, dzień po dniu', other: 'Ostatnie {count} tygodnia, dzień po dniu' },
    less: 'mniej',
    more: 'więcej',
    dailyAverage: 'Średnio dziennie',
    biggestDay: 'Najdroższy dzień',
    daysWithout: 'Dni bez wydatków',
    noSpending: 'Bez wydatków',
    openDay: 'Pokaż transakcje z dnia {date}',
    summary: 'Wydatki dzień po dniu z ostatnich 12 miesięcy. Najdroższy dzień: {date}, {amount}.',
  },
  loans: {
    intro: 'Wszystkie kredyty w jednym miejscu. Kliknij kredyt, żeby zobaczyć szczegóły: saldo w czasie, odsetki i nadpłaty.',
    add: 'Dodaj kredyt',
    empty: 'Nie masz jeszcze kredytów',
    emptyHint: 'Dodaj kredyt, żeby widzieć harmonogram rat, odsetki i datę spłaty.',
    totalPayable: 'Do spłaty łącznie',
    count: { one: '{count} kredyt', few: '{count} kredyty', many: '{count} kredytów', other: '{count} kredytu' },
    monthly: 'Raty co miesiąc',
    details: 'Szczegóły',
    suggestionsWaiting: 'Wykryto spłatę, którą można dodać jako kredyt',
    spotOverline: 'Kredyty · do spłaty',
    spotPaid: 'Spłacone',
    spotLeft: 'Zostało',
    spotNext: 'Najbliższa rata, {date}',
    spotEnd: 'Koniec spłaty',
    spotInterest: 'Odsetki do końca',
  },
  loan: {
    notFound: 'Nie ma takiego kredytu',
    apr: 'RRSO {rate}%',
    payments: '{count} rat od {date}',
    next: 'następna {amount}, {date}',
    inPayments: { one: 'w {count} racie', few: 'w {count} ratach', many: 'w {count} ratach', other: 'w {count} raty' },
    interestWhole: '{amount} przez cały kredyt',
    chartTitle: 'Saldo w czasie',
    chartLabel: 'Ile zostało do spłaty w kolejnych miesiącach',
    legendPaid: 'Spłacone',
    legendPlan: 'Plan',
    whereTitle: 'Na co poszły raty',
    whereLabel: 'Kapitał {share} zapłaconych rat',
    principal: 'Kapitał (spłacony dług)',
    interest: 'Odsetki (koszt kredytu)',
    whereHint: 'Z {paid} z {total} rat. Na początku większa część raty to odsetki, z czasem coraz więcej spłaca kapitał.',
    whatIfTitle: 'Co jeśli nadpłacę?',
    whatIfHint: 'Dopłata co miesiąc albo jednorazowa: ile odsetek zostanie w kieszeni i co się stanie z końcem kredytu. Sprawdź w umowie, czy kredytodawca pozwala nadpłacać bez opłat.',
    extraMonthly: 'Więcej co miesiąc',
    extraOnce: 'Jednorazowo',
    extraOnceDate: 'Kiedy',
    saved: 'Mniej odsetek',
    savedHint: 'niż przy racie z umowy',
    newEnd: 'Koniec spłaty',
    sameEnd: 'jak w umowie',
    monthsEarlier: { one: '{count} miesiąc wcześniej', few: '{count} miesiące wcześniej', many: '{count} miesięcy wcześniej', other: '{count} miesiąca wcześniej' },
    newFinal: 'Ostatnia rata',
    newLast: 'Ostatnia wpłata',
    insteadOf: 'zamiast {amount}',
    whatIfEmpty: 'Wpisz kwotę, żeby zobaczyć, co zmieni nadpłata.',
  },
  statementImport: {
    open: 'Importuj z pliku',
    openShort: 'Wyciąg z pliku',
    title: 'Import wyciągu z banku',
    pick: 'Wybierz plik CSV z Lloyds',
    drop: 'albo przeciągnij go tutaj',
    how: 'W bankowości Lloyds otwórz konto, kliknij „Export” nad listą transakcji i zapisz plik w formacie CSV. Pasuje też plik z Halifax i Bank of Scotland.',
    notStatement: 'To nie wygląda na wyciąg z Lloyds. Potrzebny jest plik CSV z kolumnami „Transaction Date” i „Transaction Description”.',
    unreadable: 'Nie udało się odczytać pliku.',
    empty: 'W pliku nie ma żadnych transakcji.',
    anotherFile: 'Inny plik',
    accountMask: 'konto ••{mask}',
    account: 'Do konta',
    newAccount: 'Nowe konto…',
    newName: 'Nazwa konta',
    newType: 'Rodzaj konta',
    owner: 'Czyje',
    ownerShared: 'Wspólne',
    newHint: 'Saldo początkowe weźmiemy z wyciągu, więc saldo nowego konta od razu zgodzi się z bankiem.',
    skipped: { one: 'Pominięto {count} wiersz', few: 'Pominięto {count} wiersze', many: 'Pominięto {count} wierszy', other: 'Pominięto {count} wiersza' },
    problem: {
      DATE: 'wiersz {line} (data)',
      AMOUNT: 'wiersz {line} (kwota)',
      OTHER_ACCOUNT: 'wiersz {line} (inne konto)',
    },
    countNew: { one: '{count} nowa', few: '{count} nowe', many: '{count} nowych', other: '{count} nowej' },
    countExisting: { one: '{count} już jest', few: '{count} już są', many: '{count} już jest', other: '{count} już jest' },
    rowsTitle: 'Transakcje z pliku',
    showNew: 'Tylko nowe',
    showAll: 'Wszystkie',
    dedupHint: 'To, co już jest w aplikacji (z banku albo z wcześniejszego importu), zostaje odznaczone. Gdy bank przyśle później te same płatności, połączy je z tymi z pliku i nie doda ich drugi raz.',
    statusNew: 'Nowa',
    statusExisting: 'Już jest',
    statusMaybe: 'Chyba już jest',
    existingAs: 'w aplikacji: {name}, {date}',
    nothingNew: 'Wszystko z tego pliku już jest w aplikacji.',
    add: { one: 'Dodaj {count} transakcję', few: 'Dodaj {count} transakcje', many: 'Dodaj {count} transakcji', other: 'Dodaj {count} transakcji' },
    adding: 'Dodaję… {done} z {total}',
    done: { one: 'Dodano {count} transakcję z wyciągu', few: 'Dodano {count} transakcje z wyciągu', many: 'Dodano {count} transakcji z wyciągu', other: 'Dodano {count} transakcji z wyciągu' },
  },
  forecast: {
    comingIn: 'Wpłynie',
    goingOut: 'Zejdzie',
    dailySpending: 'Codzienne wydatki dziennie ({currency})',
    scenarioHint: 'Od jutra, poza stałymi płatnościami. Puste pole to średnia z ostatnich 90 dni; wpisz inną kwotę, żeby sprawdzić inny scenariusz. Nic nie zostaje zapisane.',
    invalidAmount: 'Podaj kwotę nieujemną. Błędna kwota nie została uwzględniona.',
    title: 'Prognoza salda',
    currency: 'Waluta prognozy',
    allCash: 'Wszystkie konta bieżące · {currency}',
    horizon: 'Horyzont prognozy',
    days: '{count} dni',
    opening: 'Saldo teraz',
    closing: 'Saldo na {date}',
    balanceChart: 'Przewidywane saldo dzień po dniu',
    minimum: 'Najniższe saldo:',
    shortfall: 'Prognoza wskazuje saldo poniżej zera od {date}. Sprawdź terminy wpływów i płatności.',
    assumptions: 'Szacunek na podstawie bieżących sald i wykrytych stałych płatności. Codzienne zakupy liczymy średnią z ostatnich 90 dni; nowych, jednorazowych wydatków prognoza nie zna. Kwoty zmienne są przybliżone; saldo jest liczone na koniec dnia. Waluty są pokazywane osobno.',
    confirmedOnly: 'Tylko potwierdzone płatności',
    suggestions: 'Uwzględnione niepotwierdzone wzorce płatności: {count}.',
    review: 'Sprawdź stałe płatności',
    overdueNote: 'Wcześniejsze oczekiwane płatności ({count}) są na liście, ale nie zostały ponownie doliczone do prognozy. Sprawdź je w stałych płatnościach.',
    schedule: 'Płatności dzień po dniu',
    dayBalance: 'Na koniec dnia',
  },
  upcoming: {
    empty: 'W ciągu 30 dni nic stałego nie jest zaplanowane',
  },
  budgets: {
    heroTitle: 'Budżet · {month}',
    used: 'wykorzystane',
    ofLimit: 'z {amount}',
    left: 'Zostało {amount}',
    over: 'Przekroczony o {amount}',
    perDayFor: { one: '{amount} dziennie przez {count} dzień', few: '{amount} dziennie przez {count} dni', many: '{amount} dziennie przez {count} dni', other: '{amount} dziennie przez {count} dnia' },
    projectedUnder: 'W tym tempie miesiąc skończy się na {amount} — w budżecie',
    projectedOver: 'W tym tempie miesiąc skończy się na {amount} — ponad budżet',
    editTitle: 'Budżet',
    amount: 'Miesięcznie',
    amountInvalid: 'Podaj kwotę większą od zera',
    amountMonthly: 'Kwota na miesiąc ({currency})',
    amountInMonth: 'Kwota w miesiącu, w którym obowiązuje ({currency})',
    suggestedHint: 'Podpowiedź z historii: średnio {amount} miesięcznie w ostatnich miesiącach',
    when: 'Kiedy',
    whenMonthly: 'Co miesiąc',
    whenYearly: 'Raz w roku',
    whenMonths: 'Wybrane miesiące',
    whenMonth: 'Miesiąc płatności',
    whenFrom: 'Od',
    whenTo: 'Do',
    whenMonthlyHint: 'Kwota obowiązuje w każdym miesiącu.',
    whenYearlyHint: 'Kwota liczy się co roku tylko w tym miesiącu — w pozostałych nie zawyża limitu.',
    whenMonthsHint: {
      one: 'Kwota liczy się tylko w {count} miesiącu roku, w pozostałych nie.',
      few: 'Kwota liczy się w {count} miesiącach roku, w pozostałych nie.',
      many: 'Kwota liczy się w {count} miesiącach roku, w pozostałych nie.',
      other: 'Kwota liczy się w {count} miesiącach roku, w pozostałych nie.',
    },
    whenYearlyBadge: 'raz w roku · {month}',
    whenIdle: 'W tym miesiącu ten budżet nie obowiązuje',
    rollover: 'Przenoś resztę na następny miesiąc',
    rolloverHint: 'Co zostanie, powiększa następny miesiąc; przekroczenie go zmniejsza.',
    saved: 'Zapisano budżet',
    removed: 'Usunięto budżet',
    remove: 'Usuń budżet',
    emptyOverline: 'Budżety',
    emptyTitle: 'Ustal, ile chcesz wydawać co miesiąc',
    emptyHint: 'Limit na jedzenie, zakupy albo wszystko naraz — aplikacja pokaże, czy idziesz w tempie i ile zostało na każdy dzień.',
    fromHistory: 'Utwórz z historii',
    starterTitle: 'Budżety z Twojej historii',
    starterHint: 'Każda kwota to średnie wydatki z ostatnich pełnych miesięcy, zaokrąglone w górę do 10 £. Odznacz, czego nie chcesz; kwoty zmienisz później.',
    starterTotal: 'Razem {amount} miesięcznie',
    createCount: { one: 'Utwórz {count} budżet', few: 'Utwórz {count} budżety', many: 'Utwórz {count} budżetów', other: 'Utwórz {count} budżetu' },
    created: { one: 'Utworzono {count} budżet', few: 'Utworzono {count} budżety', many: 'Utworzono {count} budżetów', other: 'Utworzono {count} budżetu' },
    perMonth: '{amount} / mies.',
    expensesTitle: 'Wydatki',
    incomeTitle: 'Wpływy',
    colBudget: 'Budżet',
    colSpent: 'Wydano',
    colLeft: 'Zostało',
    colReceived: 'Wpłynęło',
    editCategory: 'Edytuj kategorię',
    editMain: 'Edytuj kategorię główną',
    hideCategory: 'Ukryj (zostaje w historii)',
    settings: 'Ustawienia budżetu',
    actions: 'Działania',
    subCount: { one: '{count} podkategoria', few: '{count} podkategorie', many: '{count} podkategorii', other: '{count} podkategorii' },
    rolloverShort: 'reszta na kolejny miesiąc',
    outside: 'Poza budżetem wydano {amount} (bez kwoty albo bez kategorii)',
    planHint: 'Kwotę na miesiąc wpisz w wierszu kategorii. Kategoria główna z podkategoriami sumuje ich kwoty.',
    sharedStart: 'Zawsze wspólna',
    sharedStop: 'Już nie zawsze wspólna',
    groupSharedStart: 'Wszystkie podkategorie zawsze wspólne',
    groupSharedStop: 'Podkategorie już nie zawsze wspólne',
    wholeOn: 'Jeden limit na całą kategorię',
    wholeOff: 'Osobne limity w podkategoriach',
    wholeBadge: 'jeden limit',
    wholeWithin: 'w limicie',
    wholeHint: 'Kategoria główna ma jeden limit, a wszystkie jej podkategorie liczą się do niego razem.',
    wholeOnToast: '„{name}” ma teraz jeden limit {amount} na wszystkie podkategorie.',
    wholeOffToast: '„{name}” nie ma już własnego limitu: kwoty wpisz w podkategoriach.',
    sharedBadge: 'wspólna',
    sharedHint: 'Nowe transakcje w tej kategorii (od tego miesiąca) są od razu wspólne i liczą się w rozliczeniu bez zaznaczania.',
    sharedOn: { one: '{name}: od teraz wspólna. Oznaczono też {count} transakcję z tego miesiąca.', few: '{name}: od teraz wspólna. Oznaczono też {count} transakcje z tego miesiąca.', many: '{name}: od teraz wspólna. Oznaczono też {count} transakcji z tego miesiąca.', other: '{name}: od teraz wspólna. Oznaczono też {count} transakcji z tego miesiąca.' },
    sharedOff: '{name}: nowe transakcje nie będą już wspólne z automatu; te już oznaczone zostają wspólne.',
    add: 'Dodaj',
    order: 'Kolejność',
    orderDone: 'Gotowe',
    orderHint: 'Ustaw kolejność strzałkami — tak samo ułożą się listy wyboru i Przepływy.',
    showIdle: { one: 'Pokaż jeszcze {count} kategorię — bez kwoty i wydatków', few: 'Pokaż jeszcze {count} kategorie — bez kwoty i wydatków', many: 'Pokaż jeszcze {count} kategorii — bez kwoty i wydatków', other: 'Pokaż jeszcze {count} kategorii — bez kwoty i wydatków' },
    hideIdle: 'Schowaj kategorie bez kwoty i wydatków',
  },
  overview: {
    greeting: { morning: 'Dzień dobry', afternoon: 'Dzień dobry', evening: 'Dobry wieczór' },
    greetingSub: 'Tak wyglądają Twoje finanse — {period}.',
    availableCash: 'Dostępne środki',
    availableCashHint: 'Pieniądze na rachunkach bieżących i oszczędnościowych — dostępne od ręki.',
    assets: 'Aktywa',
    assetsHint: 'Wszystko, co masz: gotówka, oszczędności, inwestycje i inny majątek, np. samochód.',
    debt: 'Zadłużenie',
    debtHint: 'Karty i kredyty — całe saldo do spłaty. Do wydatków miesiąca trafia tylko rata.',
    debtOutside: 'w tym {amount} poza majątkiem netto',
    kpis: 'Okres w liczbach',
    ofIncome: '{percent} wpływów',
    pendingPart: 'w tym {amount} oczekuje',
    pendingHint: 'Płatności oczekujące w banku: pieniądze już zablokowane — bank je zaksięguje albo zwróci.',
    noIncomeYet: 'bez wpływów w tym okresie',
    safeToSpend: 'Bezpiecznie do wydania',
    safeHint: 'Pieniądze na rachunkach bieżących po odjęciu rachunków, rat i przelewów na oszczędności zaplanowanych do końca miesiąca, z wpływami, które jeszcze przyjdą.',
    until: 'do {date}',
    perDay: '{amount} dziennie',
    nextPay: 'wypłata {date}',
    spending: 'Kategorie wydatków',
    shareOfSpending: '{percent} wydatków',
    restCategories: { one: '{count} pozostała kategoria', few: '{count} pozostałe kategorie', many: '{count} pozostałych kategorii', other: '{count} pozostałych kategorii' },
    budget: 'Budżet miesiąca',
    budgetEmpty: 'Nie masz jeszcze budżetu',
    budgetEmptyHint: 'Ustal kwoty dla kategorii — zaproponujemy je z historii.',
    spentOf: '{spent} z {limit} wydane',
    budgetPace: 'Tyle mogło być wydane do dziś przy równym tempie',
    remaining: 'Zostało {amount}',
    overBy: '{amount} ponad budżet',
    daysLeft: { one: '{count} dzień do końca miesiąca', few: '{count} dni do końca miesiąca', many: '{count} dni do końca miesiąca', other: '{count} dnia do końca miesiąca' },
    viewCalendar: 'Zobacz kalendarz',
    next14: 'Najbliższe 14 dni',
    goingOut: 'Wyjdzie w ciągu 14 dni',
    needsAttention: 'Wymaga uwagi',
    updated: 'aktualizacja {time}',
  },
  recurring: {
    title: 'Stałe płatności',
    upcoming: 'Nadchodzące płatności',
    upcomingEmpty: 'W ciągu dwóch tygodni nic nie schodzi',
    upcomingHint: 'Stałe płatności rozpoznajemy w historii, gdy powtórzą się co najmniej trzy razy.',
    more: { one: 'Razem, w tym {count} niepokazana', few: 'Razem, w tym {count} niepokazane', many: 'Razem, w tym {count} niepokazanych', other: 'Razem, w tym {count} niepokazanej' },
    overdue: 'Po terminie',
    awaitingBank: 'Czeka na bank',
    dueToday: 'Dziś',
    dueTomorrow: 'Jutro',
    dueIn: { one: 'Za {count} dzień', few: 'Za {count} dni', many: 'Za {count} dni', other: 'Za {count} dnia' },
    about: 'ok. {amount}',
    perMonth: '{amount} / mies.',
    perYear: '{amount} / rok',
    statSubscriptions: 'Subskrypcje',
    statBills: 'Rachunki i opłaty',
    statDebts: 'Raty kredytów',
    statTotal: 'Wszystkie stałe koszty',
    timelineSubtitle: 'Co już zapłacone, a co jeszcze przed tobą',
    spotOverline: 'Stałe koszty · miesięcznie',
    spotPaid: 'Zapłacone w wybranym okresie',
    spotComing: 'Jeszcze w wybranym okresie',
    inPeriod: 'W wybranym okresie: {amount}',
    notInPeriod: {
      one: '{count} stała płatność nie przypada w wybranym okresie',
      few: '{count} stałe płatności nie przypadają w wybranym okresie',
      many: '{count} stałych płatności nie przypada w wybranym okresie',
      other: '{count} stałej płatności nie przypada w wybranym okresie',
    },
    spotNext: 'Następna, {date}',
    spotAction: 'Nadchodzące płatności',
    paid: 'Zapłacone',
    upcomingShort: 'Nadchodzące',
    today: 'Dziś',
    onDay: '{day}. dnia',
    section: {
      SUBSCRIPTION: 'Subskrypcje',
      BILL: 'Rachunki i opłaty',
      DEBT: 'Raty kredytów',
      OTHER: 'Inne stałe płatności',
      INCOME: 'Stałe wpływy',
      TRANSFER: 'Stałe przelewy',
    },
    suggested: 'Wykryte automatycznie',
    confirmed: 'Potwierdzone',
    confirm: 'Potwierdź',
    dismiss: 'Odrzuć',
    restore: 'Przywróć',
    dismissedTitle: { one: 'Odrzucone ({count})', few: 'Odrzucone ({count})', many: 'Odrzucone ({count})', other: 'Odrzucone ({count})' },
    confidence: 'Pewność {value}%',
    priceUp: 'Podwyżka z {amount}',
    priceDown: 'Obniżka z {amount}',
    ended: 'Wygląda na zakończoną',
    repays: 'Spłaca: {name}',
    savedConfirm: 'Oznaczono jako stałą płatność',
    edit: 'Edytuj',
    editTitle: 'Stała płatność',
    name: 'Nazwa',
    kindLabel: 'Rodzaj',
    loan: 'Spłaca kredyt',
    loanNone: 'Żaden',
    saved: 'Zapisano zmiany',
    savedDismiss: 'Ukryto ze stałych płatności',
    savedRestore: 'Wróciło na listę',
    empty: 'Nie znaleźliśmy jeszcze stałych płatności',
    kind: {
      SUBSCRIPTION: 'Subskrypcja',
      BILL: 'Rachunek',
      DEBT: 'Rata kredytu',
      INCOME: 'Wpływ',
      TRANSFER: 'Przelew',
      OTHER: 'Stała płatność',
    },
    cadence: {
      WEEKLY: 'Co tydzień',
      FORTNIGHTLY: 'Co dwa tygodnie',
      FOUR_WEEKLY: 'Co cztery tygodnie',
      MONTHLY: 'Co miesiąc',
      QUARTERLY: 'Co kwartał',
      YEARLY: 'Co rok',
    },
  },
  transactions: {
    title: 'Transakcje',
    empty: 'Brak transakcji',
    emptyHint: 'Połącz rachunek bankowy albo dodaj pierwszą transakcję.',
    emptyFiltered: 'Żadna transakcja nie pasuje do filtrów.',
    addManually: 'Dodaj ręcznie',
    merchant: 'Sprzedawca',
    amount: 'Kwota',
    date: 'Data',
    bookedOn: 'Dzień płatności; bank zaksięgował ją {date}',
    account: 'Rachunek',
    category: 'Kategoria',
    uncategorised: 'Bez kategorii',
    kindField: 'Rodzaj',
    sender: 'Nadawca',
    recipient: 'Odbiorca',
    transferTo: 'Na moje konto',
    transferFrom: 'Z mojego konta',
    transferOutside: 'Konto spoza aplikacji',
    settlementManualHint: 'Nie liczy się jako wpływ ani wydatek. Kto komu ile oddaje, zmienia się dopiero po „Oznacz jako rozliczone” w Rozliczeniach.',
    transferHint: 'Przelew między Twoimi kontami nie liczy się do wydatków ani wpływów i nie wchodzi do rozliczeń.',
    transferPartner: 'Druga strona przelewu ({date}) na tamtym koncie też zostanie oznaczona.',
    wholeGroup: '{name}: wszystko',
    paidBy: 'Zapłacił(a)',
    paidFromAccount: 'Z rachunku (jego właściciele)',
    columnWhat: 'Transakcja',
    dayTotal: 'Razem',
    participants: 'Dla kogo',
    splitMethod: 'Podział',
    expenseType: 'Rodzaj',
    notes: 'Notatka',
    source: 'Źródło',
    status: 'Status',
    count: {
      one: '{count} transakcja',
      few: '{count} transakcje',
      many: '{count} transakcji',
      other: '{count} transakcji',
    },
    kind: {
      EXPENSE: 'Wydatek',
      INCOME: 'Wpływ',
      TRANSFER: 'Przelew własny',
      REFUND: 'Zwrot',
      SETTLEMENT_PAYMENT: 'Spłata rozliczenia',
      BALANCE_ADJUSTMENT: 'Korekta salda',
    },
    statusValue: {
      PENDING: 'Oczekuje',
      BOOKED: 'Zaksięgowana',
      REVERSED: 'Wycofana',
      CANCELLED: 'Anulowana',
    },
    sourceValue: {
      BANK: 'Bank',
      MANUAL: 'Ręcznie',
      CSV: 'Import CSV',
      OFX: 'Import OFX',
      QFX: 'Import QFX',
      CAMT: 'Import CAMT',
      SYSTEM: 'System',
    },
    searchPlaceholder: 'Szukaj sprzedawcy, opisu, notatki lub kwoty',
    allAccounts: 'Wszystkie rachunki',
    allCategories: 'Wszystkie kategorie',
    typeAll: 'Wszystko',
    typeExpenses: 'Wydatki',
    typeIncome: 'Wpływy',
    typeTransfers: 'Przelewy',
    sharedOnly: 'Tylko wspólne',
    showTransfers: 'Pokaż przelewy własne',
    clearFilters: 'Wyczyść filtry',
    allDates: 'Wszystkie daty',
    tabHistory: 'Historia',
    tabUpcoming: 'Prognoza i płatności',
    net: 'Bilans',
    netUp: 'na plus — wpływy większe niż wydatki',
    netDown: 'na minus — wydatki większe niż wpływy',
    netEven: 'wyszło na zero',
    detailsTitle: 'Transakcja',
    splitSection: 'Kogo dotyczy',
    householdRule: 'Domyślnie',
    paidByAccount: 'Z rachunku',
    splitResult: 'Podział',
    deleteConfirm: 'Usunąć tę transakcję? Zniknie na wszystkich urządzeniach.',
    deleted: 'Usunięto',
    saveChanges: 'Zapisz zmiany',
    syncState: 'Synchronizacja',
    originalDescription: 'Opis z banku',
    percentHint: 'Procenty muszą dać razem 100%',
    exactHint: 'Kwoty muszą dać razem całość',
    notInSettlement: 'Nie wchodzi do rozliczeń',
    suggestion: 'Podpowiedź: {category}',
    applyToOthers: {
      one: 'Zmień też {count} inną transakcję od „{merchant}”',
      few: 'Zmień też {count} inne transakcje od „{merchant}”',
      many: 'Zmień też {count} innych transakcji od „{merchant}”',
      other: 'Zmień też {count} innej transakcji od „{merchant}”',
    },
    appliedToOthers: {
      one: 'Zapisano — i zmieniono w {count} innej transakcji',
      few: 'Zapisano — i zmieniono w {count} innych transakcjach',
      many: 'Zapisano — i zmieniono w {count} innych transakcjach',
      other: 'Zapisano — i zmieniono w {count} innej transakcji',
    },
    uncategorisedBanner: {
      one: '{count} transakcja bez kategorii',
      few: '{count} transakcje bez kategorii',
      many: '{count} transakcji bez kategorii',
      other: '{count} transakcji bez kategorii',
    },
    knownFromHistory: 'dla {count} znamy kategorię z historii',
    fillIn: 'Uzupełnij ({count})',
    filledIn: {
      one: 'Uzupełniono kategorię {count} transakcji',
      few: 'Uzupełniono kategorie {count} transakcji',
      many: 'Uzupełniono kategorie {count} transakcji',
      other: 'Uzupełniono kategorie {count} transakcji',
    },
    badge: {
      pending: 'Oczekuje',
      shared: 'Wspólna',
      transfer: 'Przelew własny',
      refund: 'Zwrot',
    },
    shifted: {
      badge: 'przesunięta',
      WEEKEND: 'Termin {date} wypadł w weekend',
      HOLIDAY: 'Termin {date} wypadł w bank holiday',
      BOTH: 'Termin {date}: weekend i bank holiday',
      forMonth: 'płatność za {month}',
    },
  },
  quickAdd: {
    title: 'Szybkie dodawanie',
    expense: 'Wydatek',
    income: 'Wpływ',
    transfer: 'Przelew',
    from: 'Z rachunku',
    to: 'Na rachunek',
    saved: 'Zapisano',
    savedOffline: 'Zapisano na tym urządzeniu — zsynchronizuje się po powrocie sieci',
    amountInvalid: 'Wpisz kwotę, np. 12,50',
    amountTooManyDecimals: 'Za dużo miejsc po przecinku dla {currency}',
    required: 'Wymagane',
  },
  expenseType: {
    PERSONAL: 'Prywatny',
    SHARED: 'Wspólny',
    REIMBURSABLE: 'Do zwrotu',
    EXCLUDED: 'Poza rozliczeniem',
  },
  splitMethod: {
    EQUAL: 'Po równo',
    INCOME_PROPORTIONAL: 'Według dochodów',
    PERCENTAGES: 'Procentowo',
    EXACT: 'Kwotowo',
    SHARES: 'Według udziałów',
  },
  accounts: {
    title: 'Rachunki',
    add: 'Dodaj rachunek',
    empty: 'Brak rachunków',
    emptyHint: 'Dodaj rachunek ręcznie albo połącz bank.',
    name: 'Nazwa rachunku',
    institution: 'Bank lub instytucja',
    type: 'Rodzaj',
    currency: 'Waluta',
    openingBalance: 'Bieżące saldo',
    owners: 'Właściciele',
    typeValue: {
      CURRENT: 'Konto osobiste',
      SAVINGS: 'Oszczędnościowy',
      CREDIT_CARD: 'Karta kredytowa',
      CASH: 'Gotówka',
      INVESTMENT: 'Inwestycje',
      LOAN: 'Kredyt',
      MORTGAGE: 'Kredyt hipoteczny',
      ASSET: 'Inny majątek',
      LIABILITY: 'Inne zobowiązanie',
    },
    groupCards: 'Karty kredytowe',
    manual: 'Ręczny',
    bank: 'Bank',
    netWorthHint: 'Aktywa minus zobowiązania',
    loanOwed: 'Do spłaty',
    loanRepaid: 'Spłacono {share}',
    loanPayment: 'Rata',
    loanNext: 'Następna rata',
    loanPayoff: 'Koniec spłaty',
    loanPayoffNever: 'Rata nie pokrywa odsetek',
    loanRemaining: { one: 'Została {count} rata', few: 'Zostały {count} raty', many: 'Zostało {count} rat', other: 'Zostało {count} raty' },
    loanInterestLeft: 'Odsetki do końca',
    loanInterestPaid: 'Zapłacone odsetki',
    loanPrincipalPaid: 'Spłacony kapitał',
    loanOnDay: '{amount}, {day}. dnia miesiąca',
    loanOf: 'z {amount}',
    loanEstimate: 'Wyliczone z oprocentowania i wpłaconych rat; wiążący jest wyciąg od kredytodawcy.',
    loanSuggestion: 'To wygląda na ratę kredytu: {name}, {amount} co miesiąc.',
    loanSuggestionAction: 'Dodaj kredyt',
    formTitle: 'Nowy rachunek',
    formLoanTitle: 'Nowy kredyt',
    owedNow: 'Zostało do spłaty',
    principal: 'Kwota kredytu',
    rate: 'Oprocentowanie (RRSO, %)',
    payment: 'Rata miesięczna',
    paymentDay: 'Dzień spłaty',
    financeType: 'Rodzaj kredytu',
    financeTypeValue: { PERSONAL: 'Pożyczka lub kredyt gotówkowy', PCP: 'Samochód — PCP', HP: 'Samochód — HP' },
    principalHint: 'Bez wpłaty własnej — w umowie „Amount of credit”',
    paymentCount: 'Liczba rat miesięcznych',
    finalPayment: 'Ostatnia rata',
    finalPaymentPcp: 'Ostatnia rata, opcjonalna (GFV)',
    firstPaymentDate: 'Data pierwszej raty',
    paidCount: 'Zapłacone raty',
    bankSeen: {
      one: 'W banku widać {count} spłatę „{name}” od {date}.',
      few: 'W banku widać {count} spłaty „{name}” od {date}.',
      many: 'W banku widać {count} spłat „{name}” od {date}.',
      other: 'W banku widać {count} spłaty „{name}” od {date}.',
    },
    bankSeenHint: 'Jeśli wcześniejsze raty szły z innego konta, wpisz, ile jest już zapłaconych.',
    schedulePreview: 'Zapłacono {paid} z {total} rat · do spłaty łącznie {payable} · koniec {end}',
    scheduleOwedToday: 'Do wcześniejszej spłaty dziś ≈ {amount}',
    loanPayable: 'Do spłaty łącznie',
    loanOwedToday: 'Zadłużenie dziś ≈ {amount} — tyle liczy się do majątku netto; reszta to odsetki za kolejne miesiące.',
    loanPaidCount: 'Zapłacono {paid} z {total} rat',
    loanFinal: 'Ostatnia rata {amount}',
    loanPcpHint: 'PCP: ostatnią ratę możesz zapłacić i zatrzymać auto albo zamiast niej oddać auto.',
    loanHpHint: 'HP: po ostatniej racie auto jest Twoje.',
    loanScheduleEstimate: 'Wyliczone z harmonogramu umowy i RRSO; wiążący jest wyciąg od kredytodawcy.',
    assetValue: 'Wartość dziś',
    assetValueHint: 'Nowa wartość zapisze się jako korekta salda z dzisiejszą datą, więc historia majątku zostaje prawdziwa.',
    valueAdjusted: 'Nowa wartość',
    loanAsset: 'W majątku: {name}',
    assetValueShort: 'Wartość',
    loanBalanceShort: 'Do spłaty dziś',
    equity: 'Kapitał własny',
    loanAddAsset: 'Dodaj auto do majątku',
    loanAssetHint: 'Kredyt odejmuje się od majątku, a auto, za które płacisz, go równoważy. Wpisz jego wartość rynkową i poprawiaj ją co jakiś czas.',
    carName: 'Samochód',
    countInNetWorth: 'Odejmuj od majątku netto',
    countInNetWorthOn: 'Włączone: dzisiejsze zadłużenie pomniejsza majątek netto.',
    countInNetWorthOff: 'Wyłączone: majątek netto to Twoje pieniądze, a kredyt widać jako harmonogram rat do zapłacenia.',
    loanSettleToday: 'Do wcześniejszej spłaty dziś ≈ {amount}',
    loanOutside: 'Poza majątkiem netto — harmonogram zobowiązań',
    scheduleTitle: 'Harmonogram rat',
    scheduleShare: 'Kreska i procent: jaka część pozostałej do spłaty kwoty przypada na dany rok',
    scheduleCount: { one: '{count} rata', few: '{count} raty', many: '{count} rat', other: '{count} raty' },
    scheduleFinal: 'w tym ostatnia rata {amount}',
    outsideNetWorth: '+ {amount} kredytów w harmonogramie (poza majątkiem)',
    loanNeedsSchedule: 'Uzupełnij liczbę rat i datę pierwszej raty — aplikacja sama policzy, ile rat jest już za Tobą.',
    saved: 'Dodano rachunek',
    nameRequired: 'Nadaj rachunkowi nazwę',
    amountInvalid: 'Sprawdź kwoty',
    editTitle: 'Edycja rachunku',
    edit: 'Edytuj',
    changesSaved: 'Zapisano zmiany rachunku',
    archivedToast: 'Rachunek zarchiwizowany',
    delete: 'Usuń',
    deleteForever: 'Usuń na zawsze',
    deleteConfirm: {
      one: 'Usunąć „{name}” razem z {count} transakcją? Tego nie da się cofnąć.',
      few: 'Usunąć „{name}” razem z {count} transakcjami? Tego nie da się cofnąć.',
      many: 'Usunąć „{name}” razem z {count} transakcjami? Tego nie da się cofnąć.',
      other: 'Usunąć „{name}” razem z {count} transakcji? Tego nie da się cofnąć.',
    },
    deleteConfirmEmpty: 'Usunąć „{name}”? Tego nie da się cofnąć.',
    deletedToast: 'Usunięto rachunek',
    typeChangeHint: 'Wpisy z banku przeliczą się na nowo: na koncie oszczędnościowym wpłaty i wypłaty to przelewy, odsetki zostają dochodem. Zmiany wprowadzone ręcznie zostają.',
    typeChangeFailed: 'Nie udało się zmienić rodzaju rachunku — serwer jest niedostępny. Pozostałe zmiany zapisano.',
    taxWrapper: 'Opakowanie podatkowe',
    wrapperNone: 'Brak',
    wrapper: { ISA: 'ISA (Stocks & Shares lub Cash)', LISA: 'Lifetime ISA', SIPP: 'SIPP', PENSION: 'Emerytura pracownicza' },
    wrapperShort: { ISA: 'ISA', LISA: 'LISA', SIPP: 'SIPP', PENSION: 'Emerytura' },
  },
  people: {
    title: 'Osoby',
    moveUp: 'Wyżej',
    moveDown: 'Niżej',
    add: 'Dodaj osobę',
    paidShared: 'Zapłacone za wspólne',
    share: 'Należny udział',
    personalSpend: 'Wydatki prywatne',
    archive: 'Archiwizuj',
    monthlyIncome: 'Dochód miesięczny',
    thisMonth: 'W tym miesiącu',
    you: 'Ty',
    incomeShare: '{share} dochodów gospodarstwa',
    noIncome: 'Nie wpisano dochodu',
    changeIncome: 'Zmień dochód',
    setIncome: 'Wpisz dochód',
    incomeFrom: 'Od dnia',
    incomeSaved: 'Zapisano dochód',
    householdRule: 'Wspólne koszty dzielą się {rule}',
    ruleIncome: 'według dochodów ({shares})',
    ruleEqual: 'po równo',
    ruleCustom: 'według reguły gospodarstwa',
    ruleHint: 'Zmiana dochodu zmienia podział kosztów od wskazanego dnia; wcześniejsze miesiące zostają bez zmian.',
    name: 'Imię',
    color: 'Kolor',
    personSaved: 'Dodano osobę',
    accounts: 'Rachunki',
    edit: 'Edytuj osobę',
    personUpdated: 'Zapisano zmiany',
    personArchived: 'Zarchiwizowano — historia tej osoby zostaje',
    archiveHint: 'Osób się nie usuwa, bo są w historii wydatków i rozliczeń. Archiwizacja chowa je z list i nowych podziałów.',
    cannotArchiveOwner: 'To jesteś Ty — najpierw wskaż w Ustawieniach inną osobę jako „To ja”.',
    detectedTitle: 'Stałe wpływy wykryte na rachunkach',
    detectedPick: 'Zaznacz, które z nich liczą się jako dochód.',
    detectedMonthly: '{amount} / mies.',
    useDetected: 'Ustaw dochód na {amount} / mies.',
    detectedMatches: 'Zgadza się z wpisanym dochodem',
    detectedAverage: 'Kwota to średnia z tego, co faktycznie wpłynęło w ostatnich pełnych miesiącach.',
    payAccount: 'Wypłata wpływa na',
    loans: 'Kredyty i karty',
    payAccountAuto: 'Wykryj samo (rachunek tylko tej osoby)',
  },
  settlement: {
    why: 'Skąd ta kwota?',
    whySince: 'Wspólne koszty od {date}: zapłacone − udział',
    whyAll: 'Wszystkie dotychczasowe wspólne koszty: zapłacone − udział',
    whyPeriod: 'Wspólne koszty z okresu {period}: zapłacone − udział',
    itemPending: 'oczekuje w banku',
    scope: 'Co rozliczyć',
    scopePeriod: 'Okres: {period}',
    scopeAll: 'Wszystko nierozliczone',
    scopePeriodHint: 'Wspólne koszty z okresu wybranego u góry, bez tych, które już są w jakimś rozliczeniu.',
    scopeAllHint: 'Wszystkie wspólne koszty od ostatniego rozliczenia, z każdego okresu.',
    whyLegend: 'Zapłacone za wspólne koszty minus udział każdej osoby (plus zrobione już rozliczenia). Kto jest pod kreską, oddaje różnicę.',
    celebrate: 'Rozliczone — wszyscy są kwita',
    title: 'Rozliczenia',
    allSettled: 'Wszystko rozliczone',
    allSettledHint: 'Nikt nikomu nic nie jest winien.',
    sharedSoFar: 'Wspólne koszty',
    since: 'Liczone od {date}',
    neverSettled: 'jeszcze bez rozliczenia',
    paidVsShare: 'zapłacone {paid} · udział {share}',
    settleNow: 'Rozlicz się',
    open: 'Otwórz rozliczenia',
    transfer: '{from} → {to}',
    markSettled: 'Oznacz jako rozliczone',
    paidWith: 'Przelew {from} → {to} ({amount}) w banku',
    paidWithNone: 'Bez powiązania z przelewem',
    paidWithNothingFound: 'Brak pasującego przelewu z ostatnich 45 dni',
    note: 'Notatka',
    notePlaceholder: 'np. oddane gotówką, za wrzesień',
    notePresets: {
      transfer: 'Oddane przelewem za {month}',
      cash: 'Oddane gotówką za {month}',
      shopping: 'Wyrównane zakupami za {month}',
    },
    notePresetLabels: { transfer: 'Przelewem', cash: 'Gotówką', shopping: 'Zakupami' },
    pendingWarning: {
      one: '{count} płatność czeka jeszcze w banku — kwota może się zmienić.',
      few: '{count} płatności czekają jeszcze w banku — kwota może się zmienić.',
      many: '{count} płatności czeka jeszcze w banku — kwota może się zmienić.',
      other: '{count} płatności czeka jeszcze w banku — kwota może się zmienić.',
    },
    autoNote: 'Oddane przelewem za {month} (rozliczone automatycznie)',
    paidOn: 'przelew {date}',
    undo: 'Cofnij rozliczenie',
    history: 'Historia rozliczeń',
    youOweName: 'Oddajesz: {name}',
    owesYouName: 'Oddaje ci: {name}',
    owesName: '{from} oddaje: {to}',
    confirmQuestion: 'Zapisać, że zostało oddane dzisiaj? Saldo wróci do zera, a rozliczenie można cofnąć.',
    confirm: 'Tak, rozliczone',
    settledToast: 'Rozliczono',
    undoneToast: 'Rozliczenie cofnięte',
    personPaid: 'Zapłacone za wspólne',
    personShare: 'Udział w kosztach',
    personSettled: 'Wyrównane przelewami',
    netPositive: 'Należy się {amount}',
    netNegative: 'Do oddania {amount}',
    netZero: 'Na zero',
    itemsTitle: 'Od ostatniego rozliczenia',
    itemsAll: 'Wspólne koszty',
    itemsEmpty: 'Od ostatniego rozliczenia nie było wspólnych kosztów.',
    itemPaidBy: 'zapłacił(a) {name}',
    moreItems: { one: 'i jeszcze {count}', few: 'i jeszcze {count}', many: 'i jeszcze {count}', other: 'i jeszcze {count}' },
    historyEmpty: 'Jeszcze nie było rozliczeń.',
    statusSettled: 'Rozliczone',
    statusUndone: 'Cofnięte',
    lastSettled: 'Ostatnio rozliczone {date}',
    reviewTitle: 'Do decyzji',
    howItWorks: 'Wszystko, co ktoś zapłacił za wspólne koszty, minus jego udział, plus przelewy, które już coś wyrównały.',
    needsReview: {
      one: '{count} transakcja wymaga decyzji przed rozliczeniem',
      few: '{count} transakcje wymagają decyzji przed rozliczeniem',
      many: '{count} transakcji wymaga decyzji przed rozliczeniem',
      other: '{count} transakcji wymaga decyzji przed rozliczeniem',
    },
    issue: {
      NO_PAYER: 'Nie wiadomo, kto zapłacił',
      NO_PARTICIPANTS: 'Nie wybrano, kogo dotyczy ten koszt',
      INCOME_MISSING: 'Podział według dochodów, ale brak dochodów — na razie po równo',
      RATE_MISSING: 'Brak kursu waluty dla dochodu',
      INVALID_PERCENTAGES: 'Procenty nie sumują się do 100%',
      INVALID_EXACT: 'Kwoty nie sumują się do całości',
      INVALID_SHARES: 'Udziały muszą być dodatnie',
      INVALID_FUNDING: 'Zasada finansowania rachunku nie sumuje się do 100%',
      PERSONAL_WITHOUT_BENEFICIARY: 'Wydatek prywatny z konta wspólnego — wskaż, czyj jest',
      REFUND_ORIGINAL_MISSING: 'Nie znaleziono transakcji, której dotyczy zwrot',
    },
  },
  savings: {
    title: 'Oszczędności i inwestycje',
    totalValue: 'Łączna wartość',
    putAsideThisMonth: 'Odłożono w tym miesiącu',
    putAsideTaxYear: 'Odłożono w roku podatkowym {year}',
    savingsRate: 'Stopa oszczędności w tym miesiącu',
    isaTitle: 'Limit ISA {year}',
    isaUsed: 'Wpłacono {used} z {limit}',
    isaLeft: 'Zostało {amount}',
    isaDeadline: { one: 'do 5 kwietnia — został {count} dzień', few: 'do 5 kwietnia — zostały {count} dni', many: 'do 5 kwietnia — zostało {count} dni', other: 'do 5 kwietnia — zostało {count} dnia' },
    isaLisa: 'w tym Lifetime ISA: {amount} (limit {limit})',
    isaHint: 'Oznacz rachunki ISA (ołówek na ekranie Rachunki), a tu pojawi się roczny limit wpłat.',
    isaFlexibleNote: 'Liczone jak w Trading 212: ich Stocks ISA jest elastyczne, więc wypłata w tym samym roku podatkowym zwraca limit. W innych ISA wypłata limitu nie zwraca.',
    monthlyTitle: 'Odkładane co miesiąc',
    accountsTitle: 'Rachunki oszczędnościowe i inwestycyjne',
    paidInThisYear: 'Wpłacono w tym roku podatkowym: {amount}',
  },
  salary: {
    title: "Kalkulator wypłaty",
    takeHome: "Na rękę rocznie",
    takeHomeWeekly: "Na rękę tygodniowo",
    effective: "Podatek i NI razem",
    inputs: "Od stawki do wypłaty",
    note: "Wielka Brytania 2026/27. Podatek 20% do £50 270, 40% do £125 140, 45% powyżej. NI pracownika 8% do £50 270, 2% powyżej. Emerytura liczona od podstawy i premii (bez nadgodzin), przed podatkiem. To przybliżenie.",
    baseRate: "Stawka godzinowa",
    baseHoursLabel: "Godziny podstawowe w tygodniu",
    overtime: "Nadgodziny",
    bonus: "Premia roczna (razem)",
    taxes: "Podatek, NI i emerytura",
    pension: "Składka emerytalna pracownika",
    allowanceTax: "Kwota wolna od podatku",
    allowanceNI: "Próg NI (primary threshold)",
    breakdown: "Podział wypłaty",
    weekly: "Tygodniowo",
    yearly: "Rocznie",
    baseHours: "Godziny podstawowe",
    weekendOvertime: "Nadgodziny weekendowe",
    bonusRow: "Premia",
    pensionRow: "Emerytura ({pct}%)",
    taxable: "Dochód do opodatkowania",
    incomeTax: "Podatek dochodowy",
    allowanceRow: "Kwota wolna 0% (do {amount})",
    basic: "Stawka podstawowa 20%",
    higher: "Stawka wyższa 40%",
    additional: "Stawka dodatkowa 45%",
    totalTax: "Podatek razem",
    ni: "Ubezpieczenie społeczne (NI)",
    ni0: "NI 0% (do {amount})",
    ni8: "NI 8% ({from}–{to})",
    ni2: "NI 2% (powyżej {amount})",
    totalNi: "NI razem",
    net: "Na rękę",
    multiplier: "Mnożnik",
    hoursPerWeek: "Godziny w tygodniu",
    takeHomeMonthly: "Na rękę miesięcznie",
    legend: {
      net: "Na rękę",
      tax: "Podatek",
      ni: "NI",
      pension: "Emerytura",
    },
    showWeeks: "Wpisz tygodnie",
    hideWeeks: "Zwiń",
    grossRow: "Brutto",
    monthly: "Miesięcznie",
    showDetails: "Pokaż szczegóły",
    hideDetails: "Ukryj szczegóły",
    weeksTitle: "Rzeczywiste wypłaty: 52 tygodnie",
    weeksHint: "Wpisz kwotę na rękę z paska wypłaty. Różnica przy tygodniu porównuje ją z wypłatą teoretyczną z góry. Tygodnie z 0 nie liczą się. Dane zostają na tym urządzeniu.",
    filled: "Wpisane",
    total: "Razem",
    average: "Średnia",
    min: "Min",
    max: "Max",
    result: "Zysk lub strata",
    weekOne: "Piątek tygodnia 1",
    clear: "Wyczyść wszystko",
    clearConfirm: "Wyczyścić wszystkie 52 wypłaty?",
    week: "Tydz. {n}",
    compareTitle: "Porównanie",
    theory: "Prognoza teoretyczna",
    theorySub: "na rękę rocznie",
    entered: "Suma wpisanych tygodni",
    enteredSub: "{count} z 52 tygodni, na rękę",
    actual: "Prognoza z rzeczywistych wypłat",
    actualSub: "typowy (mediana) tydzień × 52",
    progress: "{pct}% roku uzupełnione ({count} z 52 tygodni)",
    bannerEmpty: "Wpisz kilka wypłat, żeby zobaczyć porównanie.",
    higherText: "Na podstawie {count} wpisanych tygodni Twoja wypłata jest wyższa niż prognoza teoretyczna ({pct}%).",
    lowerText: "Na podstawie {count} wpisanych tygodni Twoja wypłata jest niższa niż prognoza teoretyczna ({pct}%).",
  },
  simulation: {
    title: "Symulacja",
    yearTotal: "Razem w {year}",
    monthAverage: "Średnio na miesiąc",
    billsCounted: "Policzone rachunki",
    year: "Rok",
    billsTitle: "Rachunki",
    billsHint: "Rachunki liczone na cały rok. Dane zostają tylko na tym urządzeniu.",
    addBill: "Dodaj rachunek",
    empty: "Brak rachunków",
    emptyHint: "Dodaj pierwszy rachunek, a poniżej zobaczysz wyliczenie na cały rok.",
    on: "Wliczaj ten rachunek",
    name: "Nazwa",
    namePlaceholder: "np. Prąd",
    amount: "Kwota",
    frequency: "Jak często",
    monthly: "Co miesiąc",
    quarterly: "Co kwartał",
    yearly: "Raz w roku",
    inMonth: "W którym miesiącu",
    firstMonth: "Pierwszy miesiąc",
    day: "Dzień",
    who: "Kto płaci",
    perYear: "Na rok",
    remove: "Usuń rachunek",
    byPerson: "Podział na osoby",
    byPersonHint: "Każdy rachunek dzieli się po równo między zaznaczone osoby.",
    monthsTitle: "Miesiąc po miesiącu, {year}",
    calendarTitle: "Rok w tabeli",
    month: "Miesiąc",
    takenOn: "Pobrane dnia",
    monthlyShort: "miesięcznie",
    rate: "Odkładasz",
    rateHint: "dochodu zostaje po rachunkach",
    quickAdd: "Szybko dodaj",
    duplicate: "Powtórz rachunek",
    fromSalary: "Wstaw wypłatę z kalkulatora ({amount} rocznie)",
    rankTitle: "Na co idą pieniądze",
    rankHint: "Rachunki od największego, z udziałem w sumie.",
    rankHintIncome: "Rachunki od największego, z udziałem w dochodzie.",
    unnamed: "Bez nazwy",
    preset: {
      rent: "Czynsz",
      councilTax: "Council Tax",
      electricity: "Prąd",
      gas: "Gaz",
      water: "Woda",
      internet: "Internet",
      phone: "Telefon",
      carInsurance: "Ubezpieczenie auta",
      fuel: "Paliwo",
      food: "Jedzenie",
    },
    incomeYear: 'Dochód w {year}',
    savedYear: 'Zostaje na koniec {year}',
    incomeTitle: 'Dochód',
    incomeHint: 'Ile każda osoba zarabia na rękę. Rachunki odliczamy od tego, żeby policzyć, ile zostaje.',
    when: 'Kiedy',
    addPerson: 'Dodaj osobę',
    removePerson: 'Usuń osobę',
    personName: 'Imię',
    personFallback: 'Osoba {n}',
    incomeAmount: 'Dochód na rękę',
    incomePer: 'Jak często',
    per: { week: 'Tygodniowo', month: 'Miesięcznie', year: 'Rocznie' },
    noPeople: 'Brak osób',
    noPeopleHint: 'Dodaj osobę, żeby wpisać jej dochód i wybierać ją przy rachunkach.',
    addPeopleFirst: 'Najpierw dodaj osobę w dochodzie.',
    weekly: 'Co tydzień',
    weekday: 'Dzień tygodnia',
    incomeShort: 'Dochód',
    billsShort: 'Rachunki',
    left: 'Zostaje',
    builtUp: 'Narastająco',
    savedTitle: 'Ile zostaje w każdym miesiącu, {year}',
    yearOnly: 'Cały {year}',
    total: "Razem",
  },
  netWorth: {
    title: 'Majątek netto',
    assets: 'Aktywa',
    liabilities: 'Zobowiązania',
    groupCash: 'Gotówka i konta',
    groupSavings: 'Oszczędności',
    groupInvestments: 'Inwestycje',
    groupOther: 'Inny majątek',
    columnName: 'Nazwa',
    columnWeight: 'Udział',
    columnValue: 'Wartość',
    missingRate: { one: 'Pominięto {count} rachunek bez kursu waluty', few: 'Pominięto {count} rachunki bez kursu waluty', many: 'Pominięto {count} rachunków bez kursu waluty', other: 'Pominięto {count} rachunku bez kursu waluty' },
    rangeShort: { '1M': '1M', '3M': '3M', '6M': '6M', YTD: 'Od stycznia', '1Y': '1R', '5Y': '5L', ALL: 'Całość' },
    since: 'od {date}',
    chartLabel: 'Majątek netto w czasie',
    ratesNote: 'Obce waluty po dzisiejszym kursie.',
    whose: 'Czyj majątek',
    everyone: 'Razem',
    byPerson: 'Według osób',
    unassigned: 'Bez właściciela',
    jointNote: 'Rachunek liczy się jego właścicielom, a wspólny każdemu po równo.',
  },
  integrations: {
    connectionsTitle: 'Połączenia z inwestycjami',
    readOnly: 'Tylko odczyt',
    accessMode: 'Tryb dostępu',
    accountKind: 'Typ konta',
    refreshNow: 'Odśwież teraz',
    disconnect: 'Odłącz',
    apiKey: 'Klucz API',
    apiSecret: 'Sekret API',
    needsKeyAndSecret: 'Wpisz klucz i sekret API. Trading 212 pokazuje sekret tylko raz, przy tworzeniu klucza, więc jeśli go nie masz, wygeneruj nowy klucz.',
    connectHint: 'Wygeneruj w aplikacji Trading 212 klucz tylko do odczytu. Trafi zaszyfrowany na serwer i nigdy nie zostanie na tym urządzeniu.',
    status: {
      CONNECTED: 'Połączono',
      SYNCING: 'Synchronizacja…',
      ACTION_REQUIRED: 'Wymagana akcja',
      CONSENT_EXPIRING: 'Dostęp wkrótce wygaśnie',
      EXPIRED: 'Dostęp wygasł',
      CONSENT_EXPIRED: 'Do ponownego potwierdzenia',
      RATE_LIMITED: 'Limit banku — odczyt później',
      ERROR: 'Problem z synchronizacją',
      DISCONNECTED: 'Odłączono',
      UNSUPPORTED: 'Ten typ konta nie jest obsługiwany',
    },
    freshness: {
      syncedAgo: 'Zsynchronizowano {time}',
    },
    connect: 'Połącz',
    connectTitle: 'Połącz Trading 212',
    connectDemo: 'Połącz konto testowe (bez klucza)',
    environment: 'Środowisko',
    environmentLive: 'Prawdziwe konto',
    environmentDemo: 'Konto demo Trading 212',
    kindInvest: 'Invest',
    kindIsa: 'Stocks ISA',
    linkAccount: 'Rachunek w aplikacji',
    linkNew: 'Utwórz nowy',
    connected: 'Połączono z Trading 212',
    disconnected: 'Odłączono — klucz usunięty z serwera',
    refreshed: 'Odświeżono',
    keyHint: 'Klucz …{hint}',
    error: {
      INVESTMENT_UNAUTHORIZED: 'Trading 212 nie przyjął klucza albo sekretu. Sprawdź, czy są skopiowane w całości.',
      INVESTMENT_FORBIDDEN: 'Klucz nie ma uprawnień do odczytu konta, portfela i historii. Wygeneruj nowy z tymi uprawnieniami.',
      INVESTMENT_RATE_LIMITED: 'Trading 212 prosi o chwilę przerwy. Spróbuj ponownie za minutę.',
      INVESTMENT_UNAVAILABLE: 'Nie udało się połączyć z Trading 212. Spróbuj ponownie później.',
      NOT_CONFIGURED: 'Ten serwer nie ma jeszcze włączonych integracji inwestycyjnych.',
      generic: 'Nie udało się połączyć.',
    },
    positionsTitle: 'Pozycje',
    columnInstrument: 'Instrument',
    columnQuantity: 'Ilość',
    columnAverage: 'Śr. cena',
    columnPrice: 'Cena',
    columnValue: 'Wartość',
    columnResult: 'Wynik',
    inPie: 'w Pie',
    cashInPies: 'Gotówka w Pie',
    result: 'Wynik',
    resultHint: 'wartość minus to, co wpłaciłeś',
    paidIn: 'Wpłacono łącznie',
    value: 'Wartość',
    noDividends: 'Fundusze akumulujące — dywidendy zostają w funduszu i podnoszą cenę jednostki.',
  },
  palette: {
    title: 'Paleta poleceń',
    placeholder: 'Szukaj transakcji, stron i poleceń…',
    actions: 'Polecenia',
    pages: 'Przejdź do',
    transactions: 'Transakcje',
    empty: 'Nic nie pasuje do „{query}”.',
    hintMove: 'wybór',
    hintOpen: 'otwórz',
    hintClose: 'zamknij',
    addExpense: 'Dodaj wydatek',
    addIncome: 'Dodaj wpływ',
    addTransfer: 'Przelew między rachunkami',
    connectBank: 'Połącz bank',
    refreshBanks: 'Odśwież połączenia z bankami',
    refreshInvestments: 'Odśwież Trading 212',
    connectInvestments: 'Połącz Trading 212',
    themeDark: 'Włącz ciemny motyw',
    themeLight: 'Włącz jasny motyw',
    privacyOn: 'Ukryj kwoty',
    privacyOff: 'Pokaż kwoty',
    languageSwitch: 'English',
    refreshed: 'Odświeżono',
  },
  banks: {
    connect: 'Połącz bank',
    connectShort: 'Połącz bank',
    connectTitle: 'Połącz bank',
    connectHint: 'Logujesz się na stronie swojego banku i zgadzasz się tylko na odczyt: rachunki, salda i transakcje. Aplikacja nie widzi Twojego hasła i nie może robić przelewów.',
    country: 'Kraj',
    countryGB: 'Wielka Brytania',
    countryPL: 'Polska',
    search: 'Szukaj banku',
    noBanks: 'Nie ma takiego banku.',
    testBanks: 'Banki testowe z przykładowymi danymi — żaden prawdziwy bank nie jest pytany.',
    investmentsElsewhere: 'Trading 212 łączysz osobno, kluczem API tylko do odczytu:',
    investmentsLink: 'Oszczędności i inwestycje →',
    opening: 'Otwieram {bank}…',
    title: 'Połączenia z bankami',
    accountsCount: { one: '{count} rachunek', few: '{count} rachunki', many: '{count} rachunków', other: '{count} rachunku' },
    consentUntil: 'dostęp do {date}',
    lastRun: 'Ostatnie pobranie: {inserted} nowych, rozpoznane przelewy: {paired}',
    details: 'Szczegóły',
    chooseAccounts: 'Wybierz rachunki',
    pendingBanner: '{bank} pokazuje rachunki, dla których nie ma jeszcze decyzji.',
    refresh: 'Odśwież',
    reconnect: 'Połącz ponownie',
    disconnect: 'Rozłącz',
    disconnected: 'Rozłączono — rachunki i ich historia zostają, dalej prowadzone ręcznie',
    refreshed: 'Aktualne · nowych: {count}',
    expiredBanner: '{bank}: dostęp wygasł. Żeby dostawać nowe transakcje, połącz ponownie.',
    reauthBanner: 'Wymagane ponowne potwierdzenie dostępu. Historia zostaje.',
    nextRead: 'następny odczyt {time}',
    alreadyFollowed: 'Już śledzone jako „{name}”',
    expiringBanner: '{bank}: dostęp kończy się {date}. Połącz ponownie, żeby go przedłużyć.',
    errorBanner: '{bank}: ostatnie pobranie się nie udało. Spróbuj odświeżyć.',
    chooseTitle: 'Rachunki w {bank}',
    chooseHint: 'Wybierz, które rachunki śledzić i do kogo należą. Wspólne konto ma dwóch właścicieli.',
    include: 'Śledź ten rachunek',
    becomes: 'W aplikacji',
    asNew: 'Nowy rachunek',
    linkTo: 'Zamiast „{name}”',
    linkHint: 'Jego historia zostaje, a bank dopisuje transakcje od dnia po ostatnim wpisie.',
    typeHint: 'Wybierz rodzaj rachunku teraz — później nie da się go zmienić.',
    lunchFlow: {
      hint: 'Banki łączysz w Lunch Flow. Tutaj wybierasz, które z udostępnionych tam rachunków śledzić.',
      connected: 'Zsynchronizowano',
      syncing: 'Synchronizowanie…',
      stale: 'Dane mogą być nieaktualne',
      reauthorizationRequired: 'Wymagane ponowne połączenie',
      unavailable: 'Lunch Flow chwilowo niedostępny',
      lastSync: 'pobrano {time}',
      schedule: 'Aplikacja czyta Lunch Flow co godzinę, a Lunch Flow sam pobiera dane z banku kilka razy na dobę (godzin nie podaje) — płatność kartą może dojść po kilku godzinach. „Odśwież teraz” pobiera to, co Lunch Flow już ma.',
      syncNow: 'Odśwież teraz',
      syncNowHint: 'Pobierz najnowsze dane dostępne w Lunch Flow',
      synced: 'Zsynchronizowano z Lunch Flow przed chwilą · nowych: {count}',
      openLunchFlow: 'Otwórz Lunch Flow',
      reauthBanner: 'Połączenie {bank} wymaga ponownego potwierdzenia. Otwórz Lunch Flow i połącz konto ponownie.',
      unavailableBanner: 'Lunch Flow jest chwilowo niedostępny. Spróbujemy ponownie przy następnej synchronizacji.',
      expiring: 'Zgoda banku wkrótce wygaśnie',
      renewBy: 'odnowić w Lunch Flow do ok. {date}',
      expiringBanner: 'Zgoda {bank} w Lunch Flow kończy się ok. {date} (bank prosi o nią co 90 dni). Odnów ją w Lunch Flow, a potem kliknij „Odnowione”.',
      renewed: 'Odnowione',
      renewedDone: 'Zapisano odnowienie. Następne przypomnienie przed {date}.',
      accountActive: 'aktywny',
      accountReconnect: 'wymaga ponownego połączenia',
      accountTrouble: 'Lunch Flow zgłasza problem',
      accountHidden: 'już nieudostępniony w Lunch Flow',
    },
    save: 'Zapisz i pobierz',
    importing: 'Pobieram historię z {bank}…',
    added: { one: 'Dodano {count} rachunek', few: 'Dodano {count} rachunki', many: 'Dodano {count} rachunków', other: 'Dodano {count} rachunku' },
    nothingChosen: 'Nic nie wybrano — wrócisz do tego z Rachunków.',
    waiting: 'Pobieram listę rachunków…',
    errorDenied: 'Łączenie zostało przerwane w banku.',
    errorFailed: 'Bank nie potwierdził dostępu. Spróbuj jeszcze raz.',
    error: {
      BANK_UNAVAILABLE: 'Nie udało się połączyć z bankiem. Spróbuj później.',
      BANK_PROVIDER_UNAVAILABLE: 'Ten serwer nie ma jeszcze włączonego łączenia z bankami.',
      BANK_CONSENT_EXPIRED: 'Dostęp wygasł — połącz bank ponownie.',
      BANK_REFRESH_TOO_SOON: 'Odświeżono przed chwilą — spróbuj za minutę.',
      BANK_RATE_LIMITED: 'Bank pozwala teraz na niewiele odczytów; aplikacja spróbuje później.',
      BANK_ACCOUNT_ALREADY_LINKED: 'To konto jest już śledzone przez inne połączenie z tym bankiem.',
      BANK_ALREADY_CONNECTED: 'To połączenie już istnieje — nowe rachunki dodajesz przy nim.',
      BANK_SYNC_IN_PROGRESS: 'Synchronizacja już trwa — dane pojawią się za chwilę.',
      BANK_UNAUTHORIZED: 'Dostawca danych bankowych odrzucił klucz dostępu. Sprawdź konfigurację serwera.',
      BANK_FORBIDDEN: 'Dostawca danych bankowych odmówił dostępu (np. subskrypcja).',
      BANK_NOT_FOUND: 'Dostawca nie zna już tego rachunku.',
      BANK_BAD_RESPONSE: 'Dostawca danych bankowych odpowiedział nieoczekiwanie. Spróbuj później.',
      NOT_CONFIGURED: 'Ten serwer nie ma jeszcze włączonego łączenia z bankami.',
      generic: 'Nie udało się połączyć banku.',
    },
    review: {
      badge: 'Do sprawdzenia',
      duplicateTitle: 'Może wpisane dwa razy',
      duplicateText: 'Ręcznie wpisano {amount} ({merchant}, {date}). Czy to ta sama płatność, którą przysłał bank?',
      merge: 'Ta sama — zostaw bankową',
      different: 'To różne płatności',
      merged: 'Połączono — Twoja kategoria i podział zostały',
      kept: 'Zostają obie',
      transferTitle: 'Wygląda na przelew',
      transferText: 'Ta sama kwota przeszła w drugą stronę na {account} dnia {date}.',
      transferMark: 'Oznacz jako przelew',
      transferMarked: 'Oznaczono jako przelew między Twoimi rachunkami',
      pending: 'Oczekująca — bank jeszcze jej nie zaksięgował, kwota może się zmienić.',
    },
  },
  notifications: {
    title: 'Powiadomienia',
    unread: { one: '{count} nowe powiadomienie', few: '{count} nowe powiadomienia', many: '{count} nowych powiadomień', other: '{count} nowego powiadomienia' },
    empty: 'Nic nowego. Tu zobaczysz, co wymaga Twojej uwagi.',
    markAllRead: 'Oznacz wszystkie jako przeczytane',
    hide: 'Ukryj',
    settingsLink: 'Ustawienia powiadomień',
    budgetOver: 'Budżet „{name}” przekroczony: {spent} z {limit}',
    budgetOverAll: 'Budżet miesiąca przekroczony: {spent} z {limit}',
    budgetNear: 'Budżet „{name}” prawie wyczerpany: {spent} z {limit}',
    budgetNearAll: 'Budżet miesiąca prawie wyczerpany: {spent} z {limit}',
    paymentLate: '{name}: płatność nie zeszła (termin {date})',
    incomeLate: '{name}: wpływ nie przyszedł (termin {date})',
    paymentHigher: '{name}: {amount} zamiast zwykłych {usual}',
    largeExpense: 'Duży wydatek: {name}, {amount}',
    settleUp: 'Rozliczenie za {month}: {from} → {to} {amount}',
    settled: 'Rozliczenie za {month} zakończone: przelew {from} → {to} {amount} przyszedł i zapisał się sam',
    bankRenew: 'Zgoda {bank} w Lunch Flow kończy się ok. {date}',
    bankReconnect: '{bank}: połącz ponownie',
    settings: {
      budgets: 'Budżety',
      budgetsHint: 'Przy 90% i po przekroczeniu',
      payments: 'Stałe płatności',
      paymentsHint: 'Gdy nie zejdą w terminie albo wyjdą wyraźnie drożej',
      large: 'Duże wydatki',
      largeHint: 'Pojedynczy wydatek od tej kwoty',
      settlement: 'Rozliczenie miesiąca',
      settlementHint: 'W pierwszych dniach miesiąca, dopóki poprzedni nie jest rozliczony',
      banks: 'Banki',
      banksHint: 'Odnowienie zgody i problemy z połączeniem',
      system: 'Powiadomienia systemowe',
      systemHint: 'Także jako powiadomienia Windows lub przeglądarki, gdy aplikacja jest otwarta',
      systemBlocked: 'Przeglądarka blokuje powiadomienia tej strony — zezwól na nie w jej ustawieniach witryny.',
      systemUnsupported: 'Ta przeglądarka nie pokazuje powiadomień.',
    },
  },
  sync: {
    title: 'Synchronizacja',
    synced: 'Zsynchronizowano',
    pending: 'Oczekuje na synchronizację',
    syncing: 'Synchronizuję',
    failed: 'Błąd synchronizacji',
    conflict: 'Konflikt wymaga decyzji',
    offline: 'Brak sieci — zmiany zostają na tym urządzeniu',
    lastSuccess: 'Ostatnia udana synchronizacja {time}',
    pendingOperations: {
      one: '{count} zmiana czeka',
      few: '{count} zmiany czekają',
      many: '{count} zmian czeka',
      other: '{count} zmiany czeka',
    },
    failedOperations: {
      one: '{count} zmiana nie przeszła',
      few: '{count} zmiany nie przeszły',
      many: '{count} zmian nie przeszło',
      other: '{count} zmiany nie przeszło',
    },
    retry: 'Ponów',
    fullResync: 'Pełna synchronizacja',
    devices: 'Urządzenia',
    signOutDevice: 'Wyloguj to urządzenie',
    thisDevice: 'To urządzenie',
    conflictTitle: 'Ta transakcja została zmieniona na innym urządzeniu',
    serverVersion: 'Wersja na serwerze',
    yourVersion: 'Twoja wersja',
    useServer: 'Użyj wersji z serwera',
    useMine: 'Użyj mojej wersji',
    compareFields: 'Porównaj pola',
  },
  settings: {
    title: 'Ustawienia',
    household: 'Gospodarstwo',
    appearance: 'Wygląd',
    language: 'Język',
    sync: 'Synchronizacja',
    theme: 'Motyw',
    themeSystem: 'Systemowy',
    themeLight: 'Jasny',
    themeDark: 'Ciemny',
    privacyMode: 'Ukryj kwoty',
    privacyHint: 'Kwoty zamieniają się w kropki — przydaje się przy wspólnym ekranie.',
    cashEntry: 'Kwoty jak w kasie',
    cashEntryHint: 'Cyfry wchodzą od prawej: 12568 to 125,68, a 120 to 1,20. Tylko na tym urządzeniu.',
    devices: {
      title: 'Urządzenia',
      subtitle: 'Gdzie korzystasz z aplikacji na tym koncie.',
      thisDevice: 'To urządzenie',
      seen: 'ostatnio {last} · od {first}',
      forget: 'Zapomnij',
      forgotten: 'Zapomniano: {name}',
      empty: 'Lista pojawi się po pierwszej synchronizacji.',
      showAll: 'Pokaż wszystkie ({count})',
      showFewer: 'Pokaż mniej',
    },
    categories: {
      newTitle: 'Nowa kategoria',
      editTitle: 'Kategoria',
      name: 'Nazwa',
      kind: 'Rodzaj',
      icon: 'Ikona',
      color: 'Kolor',
      colorFromGroup: 'Podkategoria ma odcień koloru swojej kategorii głównej — taki sam na każdym ekranie.',
      defaultName: 'Nazwa domyślna: {name}',
      restoreName: 'Przywróć',
    groupHint: 'Podkategorie jednej kategorii głównej sumują się w Przepływach i w budżecie, a w listach wyboru stoją pod jej nazwą.',
    groupNewTitle: 'Nowa kategoria główna',
    groupEditTitle: 'Kategoria główna',
    groupName: 'Nazwa',
    groupPlaceholder: 'np. Auto albo Rachunki',
    groupSaved: 'Zapisano kategorię główną',
    groupRemove: 'Usuń kategorię główną',
    groupRemoved: 'Usunięto kategorię główną — jej podkategorie są teraz kategoriami głównymi',
      restore: 'Przywróć',
      hidden: 'Ukryte',
      nameRequired: 'Nadaj kategorii nazwę',
      saved: 'Zapisano kategorię',
      hiddenToast: 'Kategoria ukryta — transakcje ją zachowują',
      restoredToast: 'Kategoria przywrócona',
      place: 'Gdzie',
      placeMain: 'Kategoria główna',
      placeSub: 'Podkategoria w',
      delete: 'Usuń kategorię',
      deleting: 'Usuwam…',
      deleteTitle: 'Usuń: {name}',
      deleteMainTitle: 'Usuń kategorię główną: {name}',
      deleteUsed: { one: 'Kategorię {name} ma {count} transakcja. Wybierz, dokąd ją przenieść — potem kategoria zniknie na dobre.', few: 'Kategorię {name} mają {count} transakcje. Wybierz, dokąd je przenieść — potem kategoria zniknie na dobre.', many: 'Kategorię {name} ma {count} transakcji. Wybierz, dokąd je przenieść — potem kategoria zniknie na dobre.', other: 'Kategorię {name} ma {count} transakcji. Wybierz, dokąd je przenieść — potem kategoria zniknie na dobre.' },
      deleteUnused: 'Żadna transakcja nie ma kategorii {name}: zniknie od razu.',
      moveTo: 'Przenieś jej transakcje do',
      deleted: 'Usunięto: {name}',
      deleteMain: { one: 'Jej {count} podkategoria stanie się kategorią główną; transakcje zostają bez zmian. Podkategorię usuniesz z jej własnego „…”.', few: 'Jej {count} podkategorie staną się kategoriami głównymi; transakcje zostają bez zmian. Podkategorię usuniesz z jej własnego „…”.', many: 'Jej {count} podkategorii stanie się kategoriami głównymi; transakcje zostają bez zmian. Podkategorię usuniesz z jej własnego „…”.', other: 'Jej {count} podkategorii stanie się kategoriami głównymi; transakcje zostają bez zmian. Podkategorię usuniesz z jej własnego „…”.' },
      deleteMainEmpty: '{name} nie ma podkategorii: zniknie od razu.',
    },
    account: 'Konto',
    signedInAs: 'Zalogowano jako {email}',
    password: 'Hasło',
    passwordHint: 'Po zmianie inne urządzenia, np. telefon, wylogują się. Zalogujesz się na nich nowym hasłem.',
    changePassword: 'Zmień hasło',
    currentPassword: 'Obecne hasło',
    newPassword: 'Nowe hasło (co najmniej 10 znaków)',
    repeatPassword: 'Powtórz nowe hasło',
    passwordSaving: 'Zmieniam…',
    passwordTooShort: 'Nowe hasło musi mieć co najmniej 10 znaków.',
    passwordMismatch: 'Powtórzone hasło nie jest takie samo.',
    passwordSame: 'Nowe hasło musi być inne niż obecne.',
    passwordWrong: 'Obecne hasło jest nieprawidłowe.',
    passwordWeak: 'To hasło jest za słabe. Wybierz dłuższe albo mniej oczywiste.',
    passwordChanged: 'Hasło zmienione. Na innych urządzeniach zaloguj się nowym.',
    householdName: 'Nazwa gospodarstwa',
    householdSwitchHint: 'Twoje konto ma więcej niż jedno gospodarstwo; to urządzenie pokazuje wybrane tutaj.',
    baseCurrency: 'Waluta bazowa',
    ownerPerson: 'To ja',
    ownerPersonHint: 'Od tego zależy, z czyjej perspektywy pokazujemy „oddajesz” i „oddaje ci”.',
    countFrom: 'Licz od',
    countFromHint:
      'Transakcje sprzed tego dnia nie liczą się i nie są pokazywane — w wydatkach, wpływach, budżetach ani rozliczeniach — a z banku nic starszego nie jest pobierane. Zostają zapisane: wcześniejsza data je przywróci. Stałe płatności rozpoznają się także z wcześniejszych miesięcy, a inwestycje liczą się jak dotąd.',
    countFromAll: 'Licz wszystko',
    countFromSaved: 'Liczone od {date}. Urządzenia same odświeżą dane.',
    countFromCleared: 'Liczone jest wszystko. Urządzenia same odświeżą dane.',
    saved: 'Zapisano',
    syncNow: 'Synchronizuj teraz',
    state: 'Stan',
    lastSync: 'Ostatnia udana synchronizacja',
    never: 'Jeszcze nie było',
    queued: 'Zmiany w kolejce',
    failed: 'Zmiany, które nie przeszły',
    failedHint: 'Serwer odrzucił te zmiany na stałe — zwykle dlatego, że działał w starszej wersji niż aplikacja. „Ponów” wysyła wszystkie jeszcze raz; „Odrzuć” zostawia to, co jest na serwerze.',
    failedAction: { create: 'dodanie', update: 'zmiana', delete: 'usunięcie' },
    failedReason: {
      SETTLEMENT_MISMATCH: 'Salda zmieniły się, zanim rozliczenie dotarło na serwer (np. doszła płatność z banku). Odrzuć je i rozlicz jeszcze raz.',
      VALIDATION: 'Serwer nie przyjął tych danych',
      NOT_FOUND: 'Tego wpisu nie ma już na serwerze',
      REFERENCE_NOT_FOUND: 'Wskazane konto, kategoria lub osoba nie istnieje na serwerze',
      ID_TAKEN: 'Ten wpis już jest na serwerze',
      IMMUTABLE_FIELD: 'Tego pola nie można zmienić',
      READ_ONLY_ENTITY: 'Tego nie można zmieniać',
      FORBIDDEN: 'Brak uprawnień do tej zmiany',
    },
    failedEntity: {
      household: 'Gospodarstwo',
      person: 'Osoba',
      account: 'Konto',
      categoryGroup: 'Kategoria główna',
      category: 'Kategoria',
      tag: 'Etykieta',
      transaction: 'Transakcja',
      incomePeriod: 'Dochód',
      settlementRule: 'Zasada podziału',
      settlement: 'Rozliczenie',
      recurringPattern: 'Stała płatność',
      investmentConnection: 'Połączenie z inwestycjami',
      investmentPortfolio: 'Portfel',
      investmentEvent: 'Operacja w portfelu',
      bankConnection: 'Połączenie z bankiem',
      budget: 'Budżet',
      savingsGoal: 'Cel oszczędnościowy',
    },
    discard: 'Odrzuć',
    discardAll: 'Odrzuć wszystkie',
    conflicts: 'Konflikty',
    resyncHint: 'Pobiera wszystko z serwera od nowa; zmiany czekające na tym urządzeniu zostają.',
    lastError: 'Ostatni błąd: {message}',
  },
  errors: {
    network: 'Brak połączenia. Twoje zmiany są bezpieczne na tym urządzeniu.',
    validation: 'Sprawdź zaznaczone pola.',
  },
  categories: {
    housing: 'Mieszkanie',
    groceries: 'Zakupy spożywcze',
    restaurants: 'Restauracje',
    transport: 'Transport',
    shopping: 'Zakupy',
    entertainment: 'Rozrywka',
    health: 'Zdrowie',
    bills: 'Opłaty',
    subscriptions: 'Subskrypcje',
    travel: 'Podróże',
    children: 'Dzieci',
    pets: 'Zwierzęta',
    taxes: 'Podatki',
    gifts: 'Prezenty',
    education: 'Edukacja',
    salary: 'Wynagrodzenie',
    other: 'Inne',
  },
};
